import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { errorMessage, fieldError } from "@/api/errors";
import { useOrgs } from "@/api/hooks/orgs";
import { useCreateProject } from "@/api/hooks/projects";
import type { ProjectType } from "@/api/types";
import { usePageCrumbs } from "@/app/layouts/shellContext";
import { Field, FormError } from "@/components/Field";
import { MarkdownField } from "@/components/MarkdownField";
import { PageHeader } from "@/components/PageHeader";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Stepper } from "@/components/ui/stepper";
import { FileDropZone, type PickedFile } from "@/features/files/FileDropZone";
import { uploadFile } from "@/features/files/upload";
import { useOrgRole } from "@/features/projects/projectContext";
import { Markdown } from "@/lib/markdown";
import { cn } from "@/lib/utils";

const TYPES: ProjectType[] = ["new_product", "website", "mobile_app", "ai_automation", "other"];
const STEPS = ["what", "details", "files", "review"] as const;
type StepId = (typeof STEPS)[number];

function ReviewRow({ label, children, onEdit }: { label: string; children: React.ReactNode; onEdit: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2 border-b border-border py-4 last:border-b-0 sm:flex-row sm:gap-6">
      <dt className="font-semibold text-heading sm:w-48 sm:shrink-0">{label}</dt>
      <dd className="min-w-0 flex-1">{children}</dd>
      <dd>
        <Button variant="link" size="sm" onClick={onEdit} aria-label={`${t("newProject.edit")}: ${label}`}>
          {t("newProject.edit")}
        </Button>
      </dd>
    </div>
  );
}

/** Owner creates a project in four short steps; files upload after the project exists. */
export function NewProjectPage() {
  const { t } = useTranslation();
  const { org = "" } = useParams();
  const role = useOrgRole(org);
  const orgs = useOrgs();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const create = useCreateProject(org);

  const [name, setName] = useState("");
  const [type, setType] = useState<ProjectType | "">("");
  const [typeOther, setTypeOther] = useState("");
  const [description, setDescription] = useState("");
  const [goals, setGoals] = useState("");
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [progress, setProgress] = useState<{ name: string; pct: number } | null>(null);

  const orgName = orgs.data?.find((o) => o.slug === org)?.name ?? org;
  usePageCrumbs([{ label: orgName, to: `/${org}` }, { label: t("newProject.title") }]);

  const requested = params.get("step") as StepId | null;
  const step: StepId = requested && STEPS.includes(requested) ? requested : "what";
  const go = (s: StepId) => {
    setParams(s === "what" ? {} : { step: s });
    window.scrollTo?.({ top: 0 });
  };

  if (role !== null && role !== "owner" && role !== "staff") {
    return <Banner tone="warning">{t("newProject.notAllowed")}</Banner>;
  }

  const validateWhat = (): boolean => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = t("newProject.nameRequired");
    if (!type) e.type = t("newProject.typeRequired");
    if (type === "other" && !typeOther.trim()) e.typeOther = t("newProject.typeOtherRequired");
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  // Later steps need a valid first step (e.g. after a reload on ?step=review).
  if (step !== "what" && (!name.trim() || !type)) return <Navigate to={`/${org}/projects/new`} replace />;

  const next = () => {
    if (step === "what" && !validateWhat()) return;
    if (step === "files" && files.some((f) => f.error)) {
      setErrors({ files: t("newProject.filesInvalid") });
      return;
    }
    setErrors({});
    const i = STEPS.indexOf(step);
    go(STEPS[i + 1] ?? "review");
  };
  const back = () => go(STEPS[Math.max(0, STEPS.indexOf(step) - 1)] ?? "what");

  const submit = () => {
    if (!type) return;
    create.mutate(
      {
        name: name.trim(),
        project_type: type,
        ...(type === "other" ? { project_type_other: typeOther.trim() } : {}),
        description,
        goals,
      },
      {
        onSuccess: (project) => {
          void (async () => {
            let failed = 0;
            for (const { file } of files) {
              try {
                setProgress({ name: file.name, pct: 0 });
                await uploadFile(org, project.slug, file, { title: file.name }, (pct) => setProgress({ name: file.name, pct }));
              } catch {
                failed += 1;
              }
            }
            setProgress(null);
            navigate(`/${org}/${project.slug}`, { state: { created: true, failedUploads: failed } });
          })();
        },
      },
    );
  };

  const busy = create.isPending || progress !== null;
  const typeLabel = type === "other" ? typeOther.trim() || t("project.type.other") : type ? t(`project.type.${type}`) : "";
  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6">
      <PageHeader title={t("newProject.title")} description={t("newProject.intro")} />
      <Stepper
        label={t("newProject.steps.label")}
        current={step}
        steps={STEPS.map((s, i) => ({ id: s, label: t(`newProject.steps.${s}`), done: i < stepIndex }))}
      />
      <Card>
        <CardContent className="flex flex-col gap-6 pt-6">
          <p className="text-sm font-semibold text-fg-muted">{t("newProject.stepOf", { current: stepIndex + 1, total: STEPS.length })}</p>

          {step === "what" ? (
            <>
              <h2 className="text-xl">{t("newProject.steps.what")}</h2>
              <Field label={t("newProject.name")} hint={t("newProject.nameHint")} error={errors.name ?? fieldError(create.error, "name")}>
                {(p) => <Input {...p} value={name} maxLength={200} onChange={(e) => setName(e.target.value)} />}
              </Field>
              <fieldset className="flex flex-col gap-3" aria-describedby={errors.type ? "type-error" : undefined}>
                <legend className="mb-1 text-base font-medium text-fg">{t("newProject.type")}</legend>
                <div className="grid gap-3 sm:grid-cols-2">
                  {TYPES.map((v) => (
                    <label
                      key={v}
                      className={cn(
                        "flex min-h-11 cursor-pointer flex-wrap items-center gap-x-3 gap-y-1 rounded-md border px-4 py-3 transition-colors duration-fast hover:border-accent has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-focus",
                        type === v ? "border-secondary bg-secondary-subtle" : "border-border-input bg-surface",
                      )}
                    >
                      <input
                        type="radio"
                        name="project_type"
                        value={v}
                        checked={type === v}
                        onChange={() => setType(v)}
                        className="h-5 w-5 shrink-0 accent-secondary"
                      />
                      <span className="font-semibold text-heading">{t(`project.type.${v}`)}</span>
                      <span className="basis-full pl-8 text-sm text-fg-muted">{t(`newProject.typeHelp.${v}`)}</span>
                    </label>
                  ))}
                </div>
                {errors.type ? (
                  <p id="type-error" role="alert" className="text-sm font-semibold text-danger">
                    {errors.type}
                  </p>
                ) : null}
              </fieldset>
              {type === "other" ? (
                <Field label={t("newProject.typeOther")} error={errors.typeOther}>
                  {(p) => <Input {...p} value={typeOther} maxLength={200} onChange={(e) => setTypeOther(e.target.value)} />}
                </Field>
              ) : null}
            </>
          ) : null}

          {step === "details" ? (
            <>
              <h2 className="text-xl">{t("newProject.steps.details")}</h2>
              <MarkdownField
                label={t("newProject.description")}
                hint={`${t("newProject.descriptionHint")} ${t("markdown.hint")}`}
                value={description}
                onChange={setDescription}
                rows={6}
              />
              <MarkdownField label={t("newProject.goals")} hint={t("newProject.goalsHint")} value={goals} onChange={setGoals} rows={4} />
            </>
          ) : null}

          {step === "files" ? (
            <>
              <h2 className="text-xl">{t("newProject.steps.files")}</h2>
              <p className="text-fg-muted">{t("newProject.filesIntro")}</p>
              <FileDropZone files={files} onChange={setFiles} />
              {errors.files ? <FormError message={errors.files} /> : null}
            </>
          ) : null}

          {step === "review" ? (
            <>
              <h2 className="text-xl">{t("newProject.steps.review")}</h2>
              <p className="text-fg-muted">{t("newProject.reviewIntro")}</p>
              <dl>
                <ReviewRow label={t("newProject.name")} onEdit={() => go("what")}>
                  {name.trim()}
                </ReviewRow>
                <ReviewRow label={t("project.type.label")} onEdit={() => go("what")}>
                  {typeLabel}
                </ReviewRow>
                <ReviewRow label={t("newProject.description")} onEdit={() => go("details")}>
                  {description.trim() ? <Markdown source={description} /> : <span className="text-fg-muted">{t("newProject.empty")}</span>}
                </ReviewRow>
                <ReviewRow label={t("newProject.goals")} onEdit={() => go("details")}>
                  {goals.trim() ? <Markdown source={goals} /> : <span className="text-fg-muted">{t("newProject.empty")}</span>}
                </ReviewRow>
                <ReviewRow label={t("newProject.steps.files")} onEdit={() => go("files")}>
                  {files.length > 0 ? (
                    <ul className="list-disc pl-5">
                      {files.map((f, i) => (
                        <li key={`${f.file.name}-${i}`}>{f.file.name}</li>
                      ))}
                    </ul>
                  ) : (
                    <span className="text-fg-muted">{t("newProject.noFiles")}</span>
                  )}
                </ReviewRow>
              </dl>
              {create.isError && !fieldError(create.error, "name") ? (
                <FormError message={errorMessage(create.error, t("errors.generic"))} />
              ) : null}
              {progress ? (
                <p role="status" className="text-sm text-fg-muted">
                  {t("newProject.uploading", progress)}
                </p>
              ) : null}
            </>
          ) : null}

          <div className="flex flex-col-reverse gap-3 border-t border-border pt-6 sm:flex-row sm:justify-between">
            {step !== "what" ? (
              <Button variant="ghost" onClick={back} disabled={busy}>
                {t("newProject.back")}
              </Button>
            ) : (
              <span />
            )}
            {step === "review" ? (
              <Button onClick={submit} disabled={busy}>
                {busy ? t("newProject.creating") : t("newProject.create")}
              </Button>
            ) : (
              <Button variant={step === "files" && files.length === 0 ? "secondary" : "primary"} onClick={next}>
                {t("newProject.next")}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
